import * as stylex from "@stylexjs/stylex";
import { useReadScrolly } from "components/scrolly";
import { tuple } from "fp-ts/lib/function";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { Line as FixedWidthLineChart } from "../charts/lib/Lines";
import { ChartGrid } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_aid_fig_AID_TO_EDU";
import { datasets } from "../data/data_finance_aid_fig_AID_TO_EDU_datasets";
import { countryIdIso, getAidFlowName, getCountryName, mkGemDataDecoder, useMultiSelectState } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import { unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { layout } from "../materials/layout.stylex";
import { An, Ar, Eq, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_finance_aid_fig_AID_TO_EDU";
const DEFAULT_DONORS = [
    "United States",
    "United Kingdom"
];
const indicatorOrder = {
    "AID.TO.EDU": 0,
    "AID.TO.BASIC": 1,
    "AID.TO.LICS": 2
};
const Indicator = io.keyof(indicatorOrder);
const indicatorLookup = {
    "AID.TO.EDU": defineMessage({
        id: "fig.finance.aid.AID_TO_EDU.AID.TO.EDU",
        defaultMessage: "% of total aid allocated to education"
    }),
    "AID.TO.BASIC": defineMessage({
        id: "fig.finance.AID_TARGETING.AID.TO.BASIC",
        defaultMessage: "Share of basic education in total ODA to education (%)"
    }),
    "AID.TO.LICS": defineMessage({
        id: "fig.finance.AID_TARGETING.AID.TO.LICS",
        defaultMessage: "Share of ODA to low income countries in total ODA to education (%)"
    })
};
export const Data = mkGemDataDecoder([
    "value",
    "year"
], {
    ind_id: Indicator,
    donor: io.string,
    type: io.union([
        io.literal("average"),
        io.literal("entity")
    ])
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const screenLDown = !useMedia(breakpoints.l);
    const { aidflows } = useConfig();
    const { activeSection } = useReadScrolly();
    const env = useConfig();
    const [scrollyCountry, scrollyIndex] = React.useMemo(()=>{
        return pipe(activeSection, O.map((x)=>x.split(",")), O.fold(()=>[
                "all",
                -1
            ], (ids)=>{
            const [country, index] = ids;
            return [
                getCountryName(env.countries, countryIdIso.wrap(country)),
                parseInt(index)
            ];
        }));
    }, [
        activeSection,
        env.countries
    ]);
    const [selectedDonors, actions] = useMultiSelectState("donors");
    const data = React.useMemo(()=>{
        return dataRaw.map((x)=>({
                ...x,
                indicator_name: intl.formatMessage(indicatorLookup[x.ind_id]),
                donorId: x.donor,
                donor: getAidFlowName(aidflows, "donor", x.donor)
            }));
    }, [
        dataRaw,
        intl,
        aidflows
    ]);
    const averageData = React.useMemo(()=>data.filter((x)=>x.type === "average"), [
        data
    ]);
    const entityData = React.useMemo(()=>Ar.sort(Ord.contramap((x)=>tuple(indicatorOrder[x.ind_id], x.donor))(Ord.getTupleOrd(Ord.ordNumber, Ord.ordString)))(data.filter((x)=>x.type === "entity")), [
        data
    ]);
    const donorKeys = React.useMemo(()=>pipe(entityData, Ar.uniq(Eq.fromEquals((a, b)=>a.donorId === b.donorId)), unsafeFromArray, An.sort(Ord.contramap((x)=>x.donor)(Ord.ordString))), [
        entityData
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("donors", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Donors",
                defaultMessage: "Donors"
            })),
            selected: pipe(donorKeys, An.map((x)=>x.donorId), An.filter((x)=>DEFAULT_DONORS.includes(x)), O.chain(An.fromArray))
        }, donorKeys.map((x)=>({
                value: x.donorId,
                label: x.donor
            })));
    }, [
        donorKeys,
        actions,
        intl
    ]);
    const { chartData, mainLegendEntities } = React.useMemo(()=>{
        const entities = pipe(selectedDonors, O.fold(()=>[], (selected)=>entityData.filter((x)=>selected.includes(x.donorId))));
        return {
            chartData: unsafeFromArray(entities.concat(averageData)),
            mainLegendEntities: unsafeFromArray(pipe(selectedDonors, O.map((selected)=>selected.map((id)=>entityData.find((x)=>x.donorId === id))), O.fold(()=>[], (entities)=>entities.map((x)=>x.donor))))
        };
    }, [
        averageData,
        entityData,
        selectedDonors
    ]);
    const getColumn = React.useCallback((x)=>x.indicator_name, []);
    const getColor = React.useCallback((x)=>x.donor, []);
    const { colorScale, colorLegendValues } = useOrderedColorScale(O.some(mainLegendEntities));
    return <>
      <ChartGrid data={chartData} getCell={getColumn} columnCount={3} minWidth={screenMDown ? 60 : undefined}>
        {({ data, width, index })=>{
        const averageValuesOnly = pipe(data, Ar.filter((x)=>x.type === "average"), Ar.uniq(Eq.contramap(getColor)(Eq.eqString)), Ar.map((x)=>({
                label: getColor(x),
                color: M.grayscalePalette[5],
                style: "reference"
            })));
        return <>
              <FixedWidthLineChart tLabel={(s)=>s} height={screenLDown ? M.chartHeight.s : M.chartHeight.m} width={width} endLabel={!screenMDown} x="year" yTicks={[
            0,
            0.5,
            1
        ]} yNice={0} timeParse="%Y" timeFormat="%Y" numberFormat=".0%" category="datum.donor" values={data} colorLegend={false} colorScale={colorScale} showTooltip markStyle={(x)=>x.type === "average" ? "reference" : "normal"} isFaded={(d)=>scrollyCountry !== "all" && d.donor !== scrollyCountry || scrollyIndex !== -1 && index !== scrollyIndex}/>
              <ColorLegend inline maxWidth={"100%"} values={averageValuesOnly}/>
            </>;
    }}
      </ChartGrid>
      <div {...stylex.props(layout.legendSpacer)}>
        <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
      </div>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_finance_aid_fig_AID_TO_EDU.json", import.meta.url).href,
    csv: new URL("../data/data_finance_aid_fig_AID_TO_EDU.zip", import.meta.url).href,
    xlsx: new URL("../data/data_finance_aid_fig_AID_TO_EDU.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
